import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  animationFrameScheduler,
  defaultIfEmpty,
  filter,
  finalize,
  fromEvent,
  last,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useLabelingBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { ThreeState } from '@/type';
import type { NEW__LabelingBox } from '@/util';
import { filterOrRestart, subVector, withCanvasXY, withTarget } from '@/util';

import {
  applyMoveOffsetToBox,
  getIntersectObjectsByNDC,
  getIntersectPlanePointByNDC,
  isTopMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
} from '../util';

const useDefaultViewTopMove = ({
  setRenderBoxListById,
  disabled,
  isSingleMoveRef,
  isMultiMoveRef,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
  isSingleMoveRef: React.RefObject<boolean>;
  isMultiMoveRef: React.RefObject<boolean>;
}) => {
  const getThree = useThree(state => state.get as () => ThreeState);
  const { moveBoxList, selectBox, labelingBoxList } = useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);

  const getLabelingBoxById = (id: string) =>
    labelingBoxListRef.current.find(box => box.id === id);

  const isSelectedBoxId = (id: string) =>
    !!useLabelingBoxStore.getState().selectedBoxIdMap[id];

  const prepareSingleMove = ({
    toRenderBoxIdList,
  }: {
    toRenderBoxIdList: string[];
  }) => {
    setControlEnabled(false, getThree().controls);
    setRenderBoxListById(toRenderBoxIdList);
    isSingleMoveRef.current = true;
    isMultiMoveRef.current = false;
  };

  const cleanupSingleMove = ({ toSelectBoxId }: { toSelectBoxId: string }) => {
    if (!isMultiMoveRef.current) selectBox(toSelectBoxId);
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, getThree().controls);
    setRenderBoxListById([]);
    isSingleMoveRef.current = false;
  };

  const cleanupOnInvalidStream = () => {
    isSingleMoveRef.current = false;
    if (isMultiMoveRef.current) return;

    useEditingLabelBoxStore.getState().clear();
    setRenderBoxListById([]);
    setControlEnabled(true, getThree().controls);
  };

  useSubscriptions(() => {
    if (disabled) {
      cleanupSingleMove({ toSelectBoxId: '' });
      cleanupOnInvalidStream();

      return [];
    }

    const isAiAssistRunning = useToolMenuStore
      .getState()
      .isSelectedByToolName('aiAssist');

    return [
      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(() => isTopMode(useToolMainCameraStore.getState().getMode())),
          filter(() => !isAiAssistRunning),
          filter(
            () =>
              !useEditingLabelBoxStore.getState().isTransforming &&
              !useEditingLabelBoxStore.getState().isResizing,
          ),
          filter(
            e =>
              validMouseEventAndCanvasElement(e) ||
              (e.target instanceof HTMLDivElement &&
                !!e.target.ariaLabel?.match(/box-track-id/)),
          ),
          withTarget,
          withCanvasXY,
          map(e => {
            const camera = getThree().camera;

            const intersections = getIntersectObjectsByNDC({
              ndc: new Vector2(e.canvasX, e.canvasY),
              camera,
              objects: labelingBoxListRef.current.map(v => v.boxMesh),
            });

            return { event: e, intersection: intersections[0] };
          }),
          filter(
            ({ intersection }) =>
              !!intersection && !!intersection.object.userData.id,
          ),
          filter(
            ({ intersection }) =>
              !isSelectedBoxId(intersection.object.userData.id),
          ),
          map(({ intersection, event }) => {
            const mouseDownPosition = getIntersectPlanePointByNDC({
              ndc: new Vector2(event.canvasX, event.canvasY),
              camera: getThree().camera,
              coplanarPoint: intersection.point,
            });

            return {
              mouseDownPosition,
              targetBoxId: intersection.object.userData.id as string,
              targetPoint: intersection.point,
            };
          }),
          tap(({ targetBoxId }) => {
            prepareSingleMove({
              toRenderBoxIdList: [targetBoxId],
            });
          }),
          switchMap(
            ({
              mouseDownPosition,
              targetBoxId,
              targetPoint,
            }: {
              mouseDownPosition: Vector3;
              targetBoxId: string;
              targetPoint: Vector3;
            }) =>
              fromEvent<PointerEvent>(window, 'pointermove').pipe(
                withTarget,
                filterOrRestart(e => e.target instanceof HTMLCanvasElement),
                withCanvasXY,
                map(mouseMoveEvent => {
                  const mouseMovePosition = getIntersectPlanePointByNDC({
                    ndc: new Vector2(
                      mouseMoveEvent.canvasX,
                      mouseMoveEvent.canvasY,
                    ),
                    camera: getThree().camera,
                    coplanarPoint: targetPoint,
                  });

                  return {
                    mouseMovePosition,
                  };
                }),
                map(({ mouseMovePosition }) => {
                  const moveOffset = subVector(
                    mouseMovePosition,
                    mouseDownPosition,
                  );

                  return {
                    moveOffset,
                    targetBoxId,
                  };
                }),
                filterOrRestart(
                  ({ moveOffset }) =>
                    !isNaN(moveOffset.x) && !isNaN(moveOffset.y),
                ),
                tap(({ moveOffset }) => {
                  useEditingLabelBoxStore
                    .getState()
                    .setMoveOffset([moveOffset.x, moveOffset.y, 0]);
                }),
                takeUntil(fromEvent<PointerEvent>(window, 'pointerup')),
                defaultIfEmpty({ moveOffset: new Vector3() }),
                last(),
                tap(({ moveOffset, targetBoxId }) => {
                  const targetBox = getLabelingBoxById(targetBoxId);

                  if (!targetBox) return;

                  const orgPostion = targetBox.boxMesh.position.clone();

                  moveBoxList([
                    applyMoveOffsetToBox(moveOffset, orgPostion)(targetBoxId),
                  ]);

                  selectBox(targetBoxId);
                }),
                observeOn(animationFrameScheduler),
                finalize(() => {
                  cleanupSingleMove({
                    toSelectBoxId: targetBoxId,
                  });
                }),
              ),
          ),
          finalize(cleanupOnInvalidStream),
        )
        .subscribe(),
    ];
  }, [disabled]);

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);
};

export default useDefaultViewTopMove;
